// useLiveStream -- the ONLY way a page starts Live monitoring.
//
//   enabled=false / unmount -> abort the stream, clear every timer/listener
//                              (the server then stops that monitor)
//   mount / enabled         -> open the stream: first message = snapshot
//   tab hidden              -> close the stream (nothing runs server-side)
//   tab visible again       -> reopen = fresh snapshot (resync), then live
//   network drop            -> reconnect with backoff, each time a resync
//
// status: "off" | "connecting" | "live" | "paused" | "reconnecting" | "error"
import { useEffect, useRef, useState } from "react";
import { openLiveStream } from "../../auth/liveApi";
import { elakayAutoText } from "../../pageInside/dataElakay";

const BACKOFF_MS = [1000, 2000, 5000, 10000, 30000];

export const useLiveStream = ({ enabled, params, streamImpl = openLiveStream, pauseWhenHidden = true }) => {
  const [state, setState] = useState({ status: "off", data: {}, lastUpdated: null, error: null });
  const key = enabled ? JSON.stringify(params) : null;
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    if (!key) {
      setState({ status: "off", data: {}, lastUpdated: null, error: null });
      return undefined;
    }
    let controller = null;
    let retryTimer = null;
    let attempt = 0;
    let stopped = false;
    let fatal = false;
    // New params = new question: never show the previous answer meanwhile.
    setState({ status: "connecting", data: {}, lastUpdated: null, error: null });

    const connect = () => {
      if (stopped || fatal) return;
      if (pauseWhenHidden && typeof document !== "undefined" && document.visibilityState === "hidden") {
        setState((s) => ({ ...s, status: "paused" }));
        return;
      }
      const ctl = new AbortController();
      controller = ctl;
      setState((s) => ({ ...s, status: attempt ? "reconnecting" : "connecting", error: null }));
      streamImpl({
        params: paramsRef.current,
        signal: ctl.signal,
        onOpen: () => { attempt = 0; },
        onEvent: (event, payload) => {
          if (event === "hello" || !payload?.topic) return;
          if (payload.error) { setState((s) => ({ ...s, error: payload.error })); return; }
          setState((s) => ({
            ...s,
            status: "live",
            error: null,
            lastUpdated: payload.at,
            data: { ...s.data, [payload.topic]: payload.data },
          }));
        },
      })
        .then(() => { if (!stopped && !ctl.signal.aborted) scheduleRetry(); })
        .catch((error) => {
          if (stopped || error?.name === "AbortError") return;
          if (error?.status && error.status < 500 && error.status !== 429) {
            fatal = true; // 400/401/403: retrying cannot help
            setState((s) => ({ ...s, status: "error", error: error.message }));
            return;
          }
          setState((s) => ({ ...s, error: error?.message || elakayAutoText("h_live_connection_lost", "Connection lost.") }));
          scheduleRetry();
        });
    };
    const scheduleRetry = () => {
      if (stopped || fatal) return;
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      attempt += 1;
      setState((s) => ({ ...s, status: "reconnecting" }));
      clearTimeout(retryTimer);
      retryTimer = setTimeout(connect, delay);
    };
    const close = () => { controller?.abort(); controller = null; clearTimeout(retryTimer); };
    const onVisibility = () => {
      if (!pauseWhenHidden) return;
      if (document.visibilityState === "hidden") { close(); setState((s) => ({ ...s, status: "paused" })); }
      else { attempt = 0; connect(); }
    };

    connect();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      close();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [key, streamImpl, pauseWhenHidden]);

  return state;
};

export default useLiveStream;
